import type { ReactNode } from 'react';

// FilterBar — pill-button group with active state and optional count badge.
// Bundle reference: ui-kits/officer-console.html toolbar row.

export type FilterOption<T extends string = string> = {
  value: T;
  label: string;
  count?: number;
};

export type FilterBarProps<T extends string = string> = {
  options: ReadonlyArray<FilterOption<T>>;
  value: T;
  onChange: (next: T) => void;
  className?: string;
  trailing?: ReactNode;
};

export function FilterBar<T extends string = string>({
  options,
  value,
  onChange,
  className = '',
  trailing,
}: FilterBarProps<T>) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-pill text-xs font-semibold border transition-colors ${
              active
                ? 'bg-foreground text-background border-foreground'
                : 'bg-card text-foreground border-border hover:bg-muted'
            }`}
          >
            {opt.label}
            {typeof opt.count === 'number' ? (
              <span
                className={`text-[11px] tabular-nums ${
                  active ? 'text-background opacity-80' : 'text-foreground-mute'
                }`}
              >
                ({opt.count})
              </span>
            ) : null}
          </button>
        );
      })}
      {trailing ? <div className="ml-auto flex items-center gap-2">{trailing}</div> : null}
    </div>
  );
}
